import {ReactNode, useState} from "react";
import {Box, List, ListItem, Paper, Stack, Typography} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SaveIcon from "@mui/icons-material/Save";
import {useTranslation} from "react-i18next";
import {enqueueSnackbar} from "notistack";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import SettingsPageHeader from "../layout/SettingsPageHeader";
import UnsavedChangesDialog from "crqlar-react-common/dist/components/UnsavedChangesDialog";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

interface Orderable {
    id?: string;
    orderPriority?: number;
}

interface ReorderApi<T> {
    items: T[];
    dirty: boolean;
    setItems: (items: T[]) => void;
}

interface Props<T extends Orderable> {
    title: string;
    getLabel: (item: T) => string;
    save: (item: T) => Promise<unknown>;
    onClose: () => void;
    initialItems?: T[];
    controls?: (api: ReorderApi<T>) => ReactNode;
    hint?: string;
    emptyState?: ReactNode;
    busy?: boolean;
    discardMessage?: string;
}

const SortableRow = ({id, label}: {id: string; label: string}) => {
    const {attributes, listeners, setNodeRef, transform, transition} = useSortable({id});
    const style = {transform: CSS.Transform.toString(transform), transition};
    return (
        <ListItem ref={setNodeRef} style={style} disablePadding sx={{mb: 1}}>
            <Paper variant={'outlined'} sx={{width: '100%', p: 1, display: 'flex', alignItems: 'center'}}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{display: 'flex', alignItems: 'center', cursor: 'grab', '&:active': {cursor: 'grabbing'}, mr: 1}}
                >
                    <DragIndicatorIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                </Box>
                <Typography variant={'body2'} sx={{flex: 1}}>{label}</Typography>
            </Paper>
        </ListItem>
    );
};

const ReorderScreen = <T extends Orderable>({
    title,
    getLabel,
    save,
    onClose,
    initialItems,
    controls,
    hint,
    emptyState,
    busy,
    discardMessage
}: Props<T>) => {
    const {t} = useTranslation();
    const readOnly = useReadOnly();

    const [items, setItemsState] = useState<T[]>(initialItems ?? []);
    const [baseline, setBaseline] = useState<T[]>(initialItems ?? []);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    const dirty = items.map(item => item.id).join(',') !== baseline.map(item => item.id).join(',');

    const setItems = (next: T[]) => {
        setItemsState(next);
        setBaseline(next);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        if (saving || busy || readOnly) return;
        const {active, over} = event;
        if (!over || active.id === over.id) return;

        const oldIndex = items.findIndex(item => item.id === String(active.id));
        const newIndex = items.findIndex(item => item.id === String(over.id));
        if (oldIndex === -1 || newIndex === -1) return;

        setItemsState(arrayMove(items, oldIndex, newIndex).map((item, index) => ({
            ...item,
            orderPriority: index * 10
        })));
    };

    const handleSave = async () => {
        const baselinePriorityById = new Map(baseline.map(item => [item.id, item.orderPriority]));
        const changedItems = items.filter(item => baselinePriorityById.get(item.id) !== item.orderPriority);
        if (changedItems.length === 0) return;

        setSaving(true);
        try {
            await Promise.all(changedItems.map(save));
            setBaseline(items);
            enqueueSnackbar(t('Order saved'), {variant: 'success'});
        } catch {
            enqueueSnackbar(t('Could not save the new order'), {variant: 'error'});
        } finally {
            setSaving(false);
        }
    };

    const requestClose = () => {
        if (dirty) {
            setConfirmOpen(true);
            return;
        }
        onClose();
    };

    return (
        <Box sx={{width: '100%', pb: 8}}>
            <SettingsPageHeader
                title={title}
                back={requestClose}
                loading={saving || !!busy}
                actions={(
                    <LoadingButton
                        startIcon={<SaveIcon/>}
                        loading={saving}
                        disabled={!dirty || readOnly}
                        onClick={handleSave}
                        variant={'contained'}
                        color={'secondary'}
                    >
                        {t('Save')}
                    </LoadingButton>
                )}
            />

            <Stack sx={{width: '100%', maxWidth: '900px', mx: 'auto', mt: 3, gap: 2}}>
                {controls?.({items, dirty, setItems})}

                {busy ? null : items.length === 0 && emptyState ? (
                    emptyState
                ) : (
                    <>
                        {hint && (
                            <Typography variant={'caption'} color={'text.secondary'}>
                                {hint}
                            </Typography>
                        )}
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={items.map(item => item.id!)} strategy={verticalListSortingStrategy}>
                                <List sx={{width: '100%', p: 0}}>
                                    {items.map(item => (
                                        <SortableRow key={item.id} id={item.id!} label={getLabel(item)}/>
                                    ))}
                                </List>
                            </SortableContext>
                        </DndContext>
                    </>
                )}
            </Stack>

            <UnsavedChangesDialog
                open={confirmOpen}
                onConfirm={onClose}
                onCancel={() => setConfirmOpen(false)}
                message={discardMessage || 'You have unsaved reordering changes. Leave without saving?'}
            />
        </Box>
    );
};

export default ReorderScreen;
